import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';

export default function BookingSuccessPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);

  const reservationId = searchParams.get('id');

  useEffect(() => {
    if (reservationId) {
      fetchReservation();
    } else {
      setLoading(false);
    }
  }, [reservationId]);

  const fetchReservation = async () => {
    try {
      const response = await api.get(`/reservations/${reservationId}`);
      setReservation(response.data);
    } catch (error) {
      console.error('Error fetching reservation:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#5C4033]" />
      </div>
    );
  }

  return (
    <div data-testid="booking-success-page" className="min-h-screen bg-[#FAF7F2] pt-20">
      <div className="max-w-2xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-8 shadow-lg text-center"
        >
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-amber-50">
            <Clock className="h-10 w-10 text-[#C8962D]" />
          </div>

          <h1 className="font-serif text-3xl text-[#1A1A1A] mb-2">
            Solicitud Enviada
          </h1>
          <p className="text-[#1A1A1A]/60 mb-8">
            Tu solicitud de reservacion ha sido enviada exitosamente
          </p>

          {reservation && (
            <div className="bg-[#FAF7F2] rounded-xl p-6 mb-8 text-left space-y-3">
              <h3 className="font-serif text-lg text-[#1A1A1A] mb-3">Resumen</h3>
              <div className="flex justify-between text-sm">
                <span className="text-[#1A1A1A]/60">Tipo</span>
                <span className="font-medium">{reservation.reservation_type === 'hospedaje' ? 'Hospedaje' : 'Renta de Dia'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#1A1A1A]/60">Fecha</span>
                <span className="font-medium">{formatDate(reservation.check_in)}</span>
              </div>
              {reservation.check_out && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#1A1A1A]/60">Salida</span>
                  <span className="font-medium">{formatDate(reservation.check_out)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-[#1A1A1A]/60">Huespedes</span>
                <span className="font-medium">{reservation.guests}</span>
              </div>
              <div className="border-t pt-3 flex justify-between">
                <span className="text-[#1A1A1A]/60 text-sm">Total estimado</span>
                <span className="font-serif text-xl text-[#5C4033]">{formatCurrency(reservation.total)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#1A1A1A]/60">Apartado requerido</span>
                <span className="font-medium text-[#C8962D]">{formatCurrency(reservation.deposit_amount)}</span>
              </div>
            </div>
          )}

          {/* How it works */}
          <div className="bg-[#5C4033]/5 rounded-xl p-6 mb-8 text-left">
            <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Proximos pasos</h3>
            <ul className="space-y-3 text-[#1A1A1A]/70 text-sm">
              <li className="flex items-start">
                <span className="w-6 h-6 rounded-full bg-[#5C4033] text-white text-xs flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">1</span>
                Revisaremos tu solicitud y verificaremos disponibilidad
              </li>
              <li className="flex items-start">
                <span className="w-6 h-6 rounded-full bg-[#5C4033] text-white text-xs flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">2</span>
                Recibiras confirmacion con los datos bancarios para tu apartado
              </li>
              <li className="flex items-start">
                <span className="w-6 h-6 rounded-full bg-[#5C4033] text-white text-xs flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">3</span>
                Tendras 4 horas para realizar la transferencia del apartado
              </li>
              <li className="flex items-start">
                <span className="w-6 h-6 rounded-full bg-[#5C4033] text-white text-xs flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">4</span>
                Una vez confirmado el pago, tu fecha queda reservada
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/mis-reservaciones">
              <Button
                data-testid="view-reservations-btn"
                className="bg-[#5C4033] hover:bg-[#3D5A3E] text-white"
              >
                Ver Mis Reservaciones
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-[#5C4033] text-[#5C4033]">
                Volver al Inicio
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
